import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./client";

export type Category =
  | "news"
  | "earnings"
  | "score_shift"
  | "trending"
  | "insider"
  | "digest"
  | "discover"
  | "portfolio";

export type Importance = "any" | "notable" | "major";
export type Frequency = "realtime" | "daily" | "bi_daily" | "off";

export interface NotificationPref {
  category: Category;
  enabled: boolean;
  importance: Importance;
  frequency: Frequency;
  daily_hour: number | null;
  daily_hour2: number | null;
}

export const CATEGORY_META: Record<Category, { label: string; blurb: string }> = {
  news: {
    label: "News",
    blurb: "Articles mentioning a watchlist symbol.",
  },
  earnings: {
    label: "Earnings",
    blurb: "Upcoming earnings dates within 7 days.",
  },
  score_shift: {
    label: "Score shifts",
    blurb: "Bull/bear threshold crossings on the AI score.",
  },
  trending: {
    label: "Trending",
    blurb: "Watchlist symbols entering the top-50 trending list.",
  },
  insider: {
    label: "Insider trades",
    blurb: "Form-4 filings on watchlist symbols.",
  },
  digest: {
    label: "Daily digest",
    blurb: "Movers + earnings-this-week summary.",
  },
  discover: {
    label: "Discover picks",
    blurb: "AI-scored off-watchlist names entering your buy zone.",
  },
  portfolio: {
    label: "Portfolio summary",
    blurb: "Daily paper-portfolio performance vs SPY.",
  },
};

export const CATEGORY_ORDER: Category[] = [
  "news",
  "earnings",
  "score_shift",
  "trending",
  "insider",
  "digest",
  "discover",
  "portfolio",
];

export function useNotificationPrefs() {
  return useQuery<NotificationPref[]>({
    queryKey: ["notification-prefs"],
    queryFn: async () =>
      (await api.get<NotificationPref[]>("/me/notification-prefs")).data,
    staleTime: 60_000,
  });
}

export function useUpdateNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation<NotificationPref[], Error, NotificationPref[]>({
    mutationFn: async (prefs) =>
      (
        await api.patch<NotificationPref[]>("/me/notification-prefs", { prefs })
      ).data,
    onSuccess: (data) => {
      qc.setQueryData(["notification-prefs"], data);
    },
  });
}
